import ExportToExcelButton from "@app/components/export-to-excel-button";
import type { XlsxTable } from "@app/lib/export-xlsx";

import type { BudgetEditor } from "./use-budget-editor";

// The shared "Actuals vs Planned" toolbar for both overview grids: a mode toggle,
// a budget selector (New… or an existing budget for this side+year), an editable
// name, Save, Export, and Delete. Presentational — all state lives in the editor
// hook; the page supplies the page-specific xlsx builder.
export default function BudgetToolbar({
  editor,
  buildXlsx,
}: {
  editor: BudgetEditor;
  buildXlsx: () => XlsxTable;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className="inline-flex items-center rounded border border-gray-300 overflow-hidden text-xs font-semibold"
        title="Switch between actuals only and editing a budget alongside them"
      >
        <button
          type="button"
          onClick={() => editor.setMode(false)}
          className={`px-2 py-1 ${!editor.mode ? "bg-gray-900 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
        >
          Actuals
        </button>
        <button
          type="button"
          onClick={() => editor.setMode(true)}
          className={`px-2 py-1 ${editor.mode ? "bg-gray-900 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
        >
          Actuals vs Planned
        </button>
      </span>

      {editor.mode && (
        <>
          <select
            value={editor.selectedId ?? ""}
            onChange={(event) => editor.selectBudget(event.target.value || null)}
            className="rounded border border-gray-200 px-2 py-1 text-gray-700 bg-white"
            title="Load a saved budget, or start a new one"
          >
            <option value="">New budget…</option>
            {editor.budgets.map((budget) => (
              <option key={budget.id} value={budget.id}>
                {budget.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={editor.name}
            onChange={(event) => editor.setName(event.target.value)}
            placeholder="Budget name"
            className="rounded border border-gray-200 px-2 py-1 text-gray-700 w-40 focus:border-emerald-400 focus:outline-none"
          />

          <button
            type="button"
            onClick={() => editor.save()}
            disabled={editor.busy || !editor.name.trim() || (!editor.dirty && editor.selectedId !== null)}
            className="px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 whitespace-nowrap"
          >
            {editor.busy ? "Saving…" : editor.selectedId ? "Save" : "Create budget"}
          </button>

          {(editor.selectedId || editor.hasCells) && <ExportToExcelButton build={buildXlsx} />}

          {editor.selectedId && (
            <button
              type="button"
              onClick={() => editor.remove()}
              disabled={editor.busy}
              className="px-2 py-1 rounded border border-gray-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40"
            >
              Delete
            </button>
          )}
        </>
      )}
    </div>
  );
}
